import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query'
import { Box, Button, ButtonGroup, Flex, HStack, Switch, Text, VStack, useToast } from '@chakra-ui/react'
import { Bell, Check } from 'lucide-react'
import { control, type NoticeSetting } from '@services/operators'
import { apiErrorMessage } from '@utils/format'
import { EmptyState, PageHeader, Panel, SkeletonRows } from '@components/ui'
import { InboxRow, useOpenNotification } from '@components/NotificationBell'

const GROUPS: Array<{ key: NoticeSetting['group']; title: string; hint: string }> = [
  { key: 'staff', title: 'Things that need your team', hint: 'New companies, buses and documents' },
  { key: 'money', title: 'Money', hint: 'Withdrawals, payments and balances' },
  { key: 'system', title: 'System checks', hint: 'Unsettled rides, money checks, cancelled trips' },
  { key: 'company', title: 'Messages to companies', hint: 'Approvals, suspensions and cancelled trips' },
  { key: 'bus', title: 'Messages about buses', hint: 'Bus decisions and document reminders' },
]

function Settings() {
  const qc = useQueryClient()
  const toast = useToast()
  const { data, isLoading } = useQuery('notice-settings', () => control.noticeSettings().then(r => r.data))
  const [draft, setDraft] = useState<Record<string, boolean>>({})
  useEffect(() => { if (data) setDraft(Object.fromEntries(data.map(s => [s.kind, s.external]))) }, [data])
  const dirty = !!data && data.some(s => draft[s.kind] !== s.external)
  const save = useMutation(() => control.saveNoticeSettings(draft), {
    onSuccess: () => { toast({ title: 'Saved', status: 'success', duration: 2000 }); qc.invalidateQueries('notice-settings') },
    onError: e => { toast({ title: apiErrorMessage(e), status: 'error' }) },
  })

  return (
    <>
      <Text color="gray.600" fontSize="sm" mb={5} maxW="2xl">
        Every event always appears in the notification bell. Switch on <b>push &amp; email</b> to also send it outside the portal, to the people it concerns: all staff for staff events, company owners and managers (or owners and accountants for money) for company events.
      </Text>
      {isLoading ? <SkeletonRows rows={6} h={12} /> : (
        <VStack align="stretch" spacing={5}>
          {GROUPS.map(g => {
            const rows = (data ?? []).filter(s => s.group === g.key)
            if (!rows.length) return null
            return (
              <Panel key={g.key} title={g.title} subtitle={g.hint} flush>
                <VStack align="stretch" spacing={0} divider={<Box h="1px" bg="gray.100" />}>
                  {rows.map(s => (
                    <Flex key={s.kind} px={5} py={3} justify="space-between" align="center" gap={4}>
                      <Box><Text fontSize="sm" fontWeight="medium">{s.label}</Text><Text fontSize="xs" color="gray.500">{s.audience === 'staff' ? 'Goes to Daladala Smart staff' : 'Goes to the company'}</Text></Box>
                      <Switch colorScheme="brand" isChecked={!!draft[s.kind]} onChange={e => setDraft(d => ({ ...d, [s.kind]: e.target.checked }))} aria-label={`${s.label}: push and email`} />
                    </Flex>
                  ))}
                </VStack>
              </Panel>
            )
          })}
          <Flex justify="flex-end" position="sticky" bottom={4}>
            <Button isDisabled={!dirty} isLoading={save.isLoading} onClick={() => save.mutate()} shadow="lg">Save changes</Button>
          </Flex>
        </VStack>
      )}
    </>
  )
}

export default function InboxPage() {
  const [params, setParams] = useSearchParams()
  const tab = params.get('tab') === 'settings' ? 'settings' : 'feed'
  const [unreadOnly, setUnreadOnly] = useState(false)
  const qc = useQueryClient()
  const open = useOpenNotification()

  const feed = useInfiniteQuery(['inbox', 'page', unreadOnly], ({ pageParam }) => control.inbox({ unread: unreadOnly, before: pageParam, limit: 30 }).then(r => r.data), {
    getNextPageParam: last => (last.hasMore && last.items.length ? last.items[last.items.length - 1].createdAt : undefined),
    enabled: tab === 'feed', refetchInterval: 30_000,
  })
  const readAll = useMutation(() => control.markRead({ all: true }), { onSuccess: () => { qc.invalidateQueries('inbox'); qc.invalidateQueries('inbox-unread') } })
  const items = feed.data?.pages.flatMap(p => p.items) ?? []
  const unread = feed.data?.pages[0]?.unread ?? 0

  return (
    <>
      <PageHeader
        eyebrow="Control center" title="Notifications" subtitle="Everything that happened that you may need to act on."
        actions={tab === 'feed' ? <Button size="sm" variant="outline" leftIcon={<Check size={14} />} isDisabled={!unread} isLoading={readAll.isLoading} onClick={() => readAll.mutate()}>Mark all read</Button> : undefined}
      />
      <ButtonGroup isAttached variant="outline" size="sm" mb={4}>
        <Button onClick={() => setParams({})} bg={tab === 'feed' ? 'gray.900' : 'white'} color={tab === 'feed' ? 'white' : 'gray.600'} _hover={{ bg: tab === 'feed' ? 'gray.800' : 'gray.50' }}>Inbox{unread ? ` (${unread})` : ''}</Button>
        <Button onClick={() => setParams({ tab: 'settings' })} bg={tab === 'settings' ? 'gray.900' : 'white'} color={tab === 'settings' ? 'white' : 'gray.600'} _hover={{ bg: tab === 'settings' ? 'gray.800' : 'gray.50' }}>Settings</Button>
      </ButtonGroup>

      {tab === 'settings' ? <Settings /> : (
        <Panel flush>
          <HStack px={5} py={3} borderBottom="1px solid" borderColor="gray.100" spacing={3}>
            <Button size="xs" variant={unreadOnly ? 'ghost' : 'solid'} onClick={() => setUnreadOnly(false)}>All</Button>
            <Button size="xs" variant={unreadOnly ? 'solid' : 'ghost'} onClick={() => setUnreadOnly(true)}>Unread</Button>
          </HStack>
          {feed.isLoading ? <SkeletonRows rows={5} h={14} /> : items.length === 0 ? (
            <EmptyState icon={<Bell size={26} />} title={unreadOnly ? 'No unread notifications' : 'Nothing here yet'} body="New companies, buses, documents and withdrawals show up here." />
          ) : (
            <VStack align="stretch" spacing={0} divider={<Box h="1px" bg="gray.100" />}>
              {items.map(i => <InboxRow key={i.id} item={i} onOpen={open} />)}
              {feed.hasNextPage && <Flex justify="center" p={3}><Button size="sm" variant="ghost" isLoading={feed.isFetchingNextPage} onClick={() => feed.fetchNextPage()}>Load older</Button></Flex>}
            </VStack>
          )}
        </Panel>
      )}
    </>
  )
}
